import type { SearchCoffeeResult } from '@/types/search';

import { CantFindItCTA } from './CantFindItCTA';
import { SearchResultRow } from './SearchResultRow';

type SearchDropdownLayout = 'floating' | 'inline';

interface SearchDropdownProps {
  query: string;
  results: SearchCoffeeResult[];
  isLoading: boolean;
  highlightedIndex: number;
  onSelect: (result: SearchCoffeeResult) => void;
  onViewAll: () => void;
  // The current dropdown attempt's `search_id` (#448), for the request CTA.
  searchId: string;
  // `floating` is the desktop popover; `inline` is the mobile full-screen
  // surface, which renders results as a flowing list instead of a panel.
  layout?: SearchDropdownLayout;
}

export function SearchDropdown({
  query,
  results,
  isLoading,
  highlightedIndex,
  onSelect,
  onViewAll,
  searchId,
  layout = 'floating',
}: SearchDropdownProps) {
  const inline = layout === 'inline';
  const containerClass = inline
    ? 'px-4 py-4'
    : 'absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-xl bg-wolisho-card';

  return (
    <div className={containerClass}>
      {isLoading && results.length === 0 && (
        <div className="px-4 py-6 text-center text-sm text-black/60">Searching…</div>
      )}

      {!isLoading && results.length === 0 && (
        <div className="px-4 py-6 text-center text-sm text-black/60">
          No matches for “{query}”
          <CantFindItCTA query={query} variant="dropdown-empty" searchId={searchId} />
        </div>
      )}

      {results.length > 0 && (
        <>
          {inline ? (
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {results.map((result) => (
                <li key={result.id}>
                  <SearchResultRow
                    result={result}
                    variant="card"
                    onSelect={() => {
                      onSelect(result);
                    }}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <ul className="m-0 list-none p-0">
              {results.map((result, index) => (
                <li
                  key={result.id}
                  className={
                    index < results.length - 1 ? 'border-b border-wolisho-brown/10' : undefined
                  }
                >
                  <SearchResultRow
                    result={result}
                    variant="dropdown"
                    highlighted={index === highlightedIndex}
                    onSelect={() => {
                      onSelect(result);
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
          <div
            className={
              inline ? 'px-1 pt-4' : 'border-t border-wolisho-brown/10 px-4 py-3'
            }
          >
            <button
              type="button"
              onClick={onViewAll}
              className="block w-full text-left text-sm font-bold text-wolisho-red hover:underline"
            >
              View all results for “{query}” →
            </button>
            <CantFindItCTA query={query} variant="dropdown-footer" searchId={searchId} />
          </div>
        </>
      )}
    </div>
  );
}
